import {
  BackupAndRestoreStatus,
  BackupChildConnectionStatus,
  BackupChildHealthStatus,
  WORKLOAD_HEALTH_SEVERITY,
  WORKLOAD_TO_CHILD_HEALTH,
  WorkloadConnectionStatus,
  WorkloadHealthStatus,
  type BackupChild,
  type BackupChildWorkload,
  type BackupConnector,
} from "@/constants/cloud-backup.ts";

/**
 * A child scheduled for deletion is no longer synced: its workloads are frozen at the
 * last state seen before removal was decided, and it can neither be configured nor
 * opened in Keepit.
 */
export function isBackupChildPendingDeletion(item: BackupChild): boolean {
  return item.status === BackupAndRestoreStatus.PENDING_DELETION;
}

export function isWorkloadConnected(workload: BackupChildWorkload): boolean {
  return workload.status === WorkloadConnectionStatus.CONNECTED;
}

// Rolls a child workspace's workloads up to a single connection status.
export function getBackupChildConnectionStatus(item: BackupChild): BackupChildConnectionStatus {
  const total = item.workloads.length;
  const connected = item.workloads.filter(isWorkloadConnected).length;

  if (total > 0 && connected === total) {
    return BackupChildConnectionStatus.CONNECTED;
  }
  if (connected > 0) {
    return BackupChildConnectionStatus.PARTIAL;
  }
  return BackupChildConnectionStatus.NOT_CONNECTED;
}

// Rolls a child workspace's connected workloads up to a single health status
// (the highest severity wins); NONE when no workload is connected.
export function getBackupChildHealthStatus(item: BackupChild): BackupChildHealthStatus {
  const connectedWorkloads = item.workloads.filter(isWorkloadConnected);
  if (!connectedWorkloads.length) {
    return BackupChildHealthStatus.NONE;
  }

  const worstHealth = connectedWorkloads.reduce(
    (worst, workload) =>
      WORKLOAD_HEALTH_SEVERITY[workload.healthStatus] > WORKLOAD_HEALTH_SEVERITY[worst]
        ? workload.healthStatus
        : worst,
    WorkloadHealthStatus.HEALTHY
  );

  return WORKLOAD_TO_CHILD_HEALTH[worstHealth];
}

/**
 * Health of a single connector as the Connected apps table reports it.
 *
 * A disconnected app has no backups running, so whatever health the backend last
 * recorded for it says nothing: NONE is reported instead. `WorkloadHealthStatus`
 * has no such member, hence the roll-up to `BackupChildHealthStatus` — the same
 * rule `getBackupChildHealthStatus` applies to a whole workspace.
 */
export function getConnectorHealthStatus(connector: BackupConnector): BackupChildHealthStatus {
  if (!isWorkloadConnected(connector)) {
    return BackupChildHealthStatus.NONE;
  }

  return WORKLOAD_TO_CHILD_HEALTH[connector.healthStatus];
}
